import useUserUiPreferenceStorageValue from "../hooks/useUserUiPreferenceStorageValue.js";
import useLiveUserUiPreference from "../hooks/useLiveUserUiPreference.js";
import React from "react";
import { productUpdatesRequest } from "../lib/productUpdatesApi.js";
import { userUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
const EFFORT_LABELS = { none: "Aucun", minimal: "Minimal", low: "Faible", medium: "Moyen", high: "Élevé", xhigh: "Très élevé", max: "Maximal", ultra: "Ultra" };

const ENGINE_NAMES = { codex: "Codex", claude: "Claude" };
const ENGINE_KEY = scope => `journal-local-engine-${scope}`;

function savedEngine(scope, engines) {
  try { const value = userUiPreferenceStorage("local")?.getItem(ENGINE_KEY(scope)); if (engines.includes(value)) return value; } catch { /* Stockage indisponible. */ }
  return engines[0];
}

export default function ProductUpdatesCodexSettings({ busy = false, onChange, scope = "texts", engines = ["codex"] }) {
  const [engine, setEngine] = React.useState(() => savedEngine(scope, engines));
  const name = ENGINE_NAMES[engine];
  const [models, setModels] = React.useState([]);
  const [model, setModel] = React.useState("");
  const [effort, setEffort] = React.useState("");
  const [codexFast, setFast] = React.useState(true);
  const fast = engine === "codex" && codexFast;
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const storedEngine = useUserUiPreferenceStorageValue("local", ENGINE_KEY(scope));
  const journal = useLiveUserUiPreference("journal", null, busy || saving || loading);
  React.useEffect(() => {
    if (!busy && !saving) setEngine(savedEngine(scope, engines));
  }, [storedEngine, busy, saving, scope]);
  React.useEffect(() => {
    if (!journal || busy || saving || loading) return;
    const prefix = `journal_${engine}${scope === "image" ? "_image" : ""}`;
    if (journal[`${prefix}_model`]) setModel(journal[`${prefix}_model`]);
    if (journal[`${prefix}_effort`]) setEffort(journal[`${prefix}_effort`]);
  }, [journal]);
  const [error, setError] = React.useState("");
  const active = React.useRef(true);
  const selected = models.find(row => row.id === model);
  const efforts = selected?.efforts || (effort ? [effort] : ["none"]);
  const ready = !loading && !saving && !error && Boolean(selected) && efforts.includes(effort) && (!fast || selected.fast_supported);
  React.useEffect(() => { if (!engines.includes(engine)) setEngine(engines[0]); }, [engines, engine]);
  React.useEffect(() => onChange({ engine, model, effort, fast, ready }), [engine, model, effort, fast, ready, onChange]);
  const load = React.useCallback(async (refresh = false) => {
    setLoading(true); setError(""); setModels([]); setModel(""); setEffort("");
    try {
      const params = new URLSearchParams();
      if (refresh) params.set("refresh", "true");
      if (scope === "image") params.set("scope", "image");
      if (engine !== "codex") params.set("engine", engine);
      const data = await productUpdatesRequest(`/codex/models${params.size ? `?${params}` : ""}`);
      if (active.current) {
        setModels(data.models); setModel(data.selected_model);
        setEffort(data.selected_effort || data.models.find(row => row.id === data.selected_model)?.default_effort || "none");
      }
    } catch (failure) { if (active.current) setError(failure.message); }
    finally { if (active.current) setLoading(false); }
  }, [scope, engine]);
  React.useEffect(() => { active.current = true; void load(); return () => { active.current = false; }; }, [load]);

  async function choose(value, nextEffort) {
    const previous = model, previousEffort = effort;
    const nextModel = models.find(row => row.id === value);
    nextEffort = nextEffort || (nextModel?.efforts?.includes(effort) ? effort : nextModel?.default_effort || "none");
    onChange({ engine, model: value, effort: nextEffort, fast, ready: false });
    setModel(value); setEffort(nextEffort); setSaving(true); setError("");
    try {
      const result = await productUpdatesRequest("/codex/model", { method: "PATCH", body: { model: value, effort: nextEffort, ...(scope === "image" ? { scope } : {}), ...(engine !== "codex" ? { engine } : {}) } });
      if (active.current) { setModel(result.selected_model); setEffort(result.selected_effort || nextEffort); }
    } catch (failure) { if (active.current) { setModel(previous); setEffort(previousEffort); setError(failure.message); } }
    finally { if (active.current) setSaving(false); }
  }

  return <fieldset className="pu-source pu-codex-settings" disabled={busy || loading || saving}>
    <legend>{engines.length > 1 ? (scope === "image" ? "IA pour les visuels" : "IA sur ce poste") : `${name} ${scope === "image" ? "pour les visuels" : "sur ce poste"}`}</legend>
    <div className="pu-actions">
      {engines.length > 1 && <label>Outil<select aria-label={scope === "image" ? "Outil pour le visuel" : "Outil"} value={engine} onChange={event => {
        const next = event.target.value;
        try { userUiPreferenceStorage("local")?.setItem(ENGINE_KEY(scope), next); } catch { /* Stockage indisponible. */ }
        setEngine(next);
      }}>
        {engines.map(value => <option key={value} value={value}>{ENGINE_NAMES[value]} (abonnement {value === "claude" ? "Claude" : "ChatGPT"})</option>)}
      </select></label>}
      <label>{scope === "image" ? `Modèle ${name} pour le visuel` : `Modèle ${name}`}<select aria-label={scope === "image" ? `Modèle ${name} pour le visuel` : `Modèle ${name}`} value={model} onChange={event => void choose(event.target.value)}>
        {!models.length && <option value="">{loading ? "Chargement des modèles…" : "Catalogue indisponible"}</option>}
        {model && !selected && <option value={model}>{model} — indisponible</option>}
        {models.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
      </select></label>
      <label>Niveau d'effort<select aria-label={scope === "image" ? "Niveau d'effort pour le visuel" : "Niveau d'effort"} value={effort} onChange={event => void choose(model, event.target.value)}>
        {!effort && <option value="">Chargement…</option>}
        {efforts.map(level => <option key={level} value={level}>{EFFORT_LABELS[level] || level}</option>)}
      </select></label>
      <button type="button" className="pu-button" onClick={() => void load(true)}>{scope === "image" ? "Actualiser les modèles pour le visuel" : "Actualiser les modèles"}</button>
      {engine === "codex" && <label className="pu-codex-fast"><input type="checkbox" aria-label={scope === "image" ? "Mode rapide pour le visuel" : "Mode rapide"} checked={fast} onChange={event => setFast(event.target.checked)} />Mode rapide</label>}
    </div>
    <small>{saving ? "Enregistrement des choix…" : "Le modèle et le niveau d'effort choisis sont conservés pour les prochaines générations et régénérations."}</small>
    {!loading && model && !selected && <p className="pu-warning">Le dernier modèle choisi n'est plus dans le catalogue. Actualisez la liste ou choisissez un autre modèle.</p>}
    {selected && fast && !selected.fast_supported && <p className="pu-warning">Ce modèle ne propose pas le mode rapide. Choisissez un autre modèle ou décochez ce mode pour cette génération.</p>}
    {error && <p role="alert" className="pu-error">{error} <button type="button" className="pu-button" disabled={busy || loading || saving} onClick={() => void load(true)}>Réessayer</button></p>}
  </fieldset>;
}
